/**
 * Carrinho — as 8 operacoes do bloco "Carrinho" (`API §6.9`), e o unico lugar do produto onde
 * `ETag`/`If-Match` e assunto de alguem.
 *
 * ## O ciclo, inteiro, em quatro linhas
 *
 * ```text
 *  1. toda resposta de carrinho traz `ETag` — inclusive as de mutacao (`cart.controller.ts`)
 *  2. o cliente guarda esse `ETag` em `ResourceVersionStore`
 *  3. toda mutacao manda `If-Match` com o que guardou
 *  4. `409 CART_VERSION_CONFLICT` traz o carrinho atual em `meta.current`: o cliente
 *     RECARREGA a versao a partir dele e NAO reenvia a escrita
 * ```
 *
 * O passo 4 e o que o briefing pede com todas as letras ("trata 409 recarregando em vez de
 * sobrescrever") e o que `API §4.2` justifica: reenviar seria o last-write-wins que o documento
 * descarta — *"e exatamente o bug que faz um balconista desfazer o trabalho do outro sem
 * ninguem perceber"*.
 *
 * ## Sobre o `If-Match` da primeira escrita
 *
 * Quando o cliente ainda nao viu carrinho nenhum, `current('cart')` e `null` e **nenhum**
 * `If-Match` sai. Isso nao e um furo: `CartWorkspace.assertNoIfMatchWhenAbsent` do servidor
 * exige exatamente o contrario — `If-Match` presente sem carrinho e conflito, porque o cliente
 * afirma uma versao de algo que nao existe. As duas pontas concordam por construcao.
 *
 * ## Uma ausencia que e contrato
 *
 * Nenhum schema de carrinho tem campo de dinheiro (`public/cart.ts`: *"a fronteira de X-38
 * escrita no tipo"*). O carrinho guarda intencao; valor vem de `POST /v1/checkout/quote`. Nao
 * ha nada a somar aqui, e por isso nao ha nada somado.
 */
import {
  CartResponseSchema,
  cartWeakETag,
  type AddCartItemRequest,
  type ApplyCouponRequest,
  type CartResponse,
  type MergeCartRequest,
  type PatchCartItemRequest,
  type ReplaceCartRequest,
} from '@farmacia/contracts';

import type { ApiClientCore } from '../core.js';
import { CartVersionConflictError } from '../errors.js';
import type { ApiRequestSpec, ApiResponse } from '../http.js';

export interface CartApi {
  get: (signal?: AbortSignal) => Promise<CartResponse>;
  /** `PUT /v1/cart` — o espelho da fila `outbox` (`PWA §8.1`, `§8.4`). */
  replace: (body: ReplaceCartRequest) => Promise<CartResponse>;
  addItem: (body: AddCartItemRequest) => Promise<CartResponse>;
  changeItem: (itemId: string, body: PatchCartItemRequest) => Promise<CartResponse>;
  removeItem: (itemId: string) => Promise<CartResponse>;
  merge: (body: MergeCartRequest) => Promise<CartResponse>;
  applyCoupon: (body: ApplyCouponRequest) => Promise<CartResponse>;
  removeCoupon: (code: string) => Promise<CartResponse>;
  /** A versao que o cliente esta afirmando agora. Existe para o teste e para o diagnostico. */
  currentETag: () => string | null;
}

export function createCartApi(core: ApiClientCore): CartApi {
  /**
   * Uma leitura: guarda o `ETag` e devolve o corpo.
   *
   * O `remember` acontece **em toda** resposta de sucesso, e nao so no `GET`. E o que permite
   * encadear duas escritas sem um `GET` no meio — o `cart.controller.ts` da API poe o `ETag`
   * na resposta de mutacao exatamente por isso ("essencial para a fila offline, que precisa da
   * versao nova para o proximo item da `outbox`").
   */
  async function run(spec: ApiRequestSpec<CartResponse>): Promise<CartResponse> {
    try {
      const response: ApiResponse<CartResponse> = await core.request(spec);
      core.versions.remember('cart', response.etag);
      return response.data;
    } catch (error) {
      if (error instanceof CartVersionConflictError) {
        // RECARREGA: a versao passa a ser a que o servidor acabou de mostrar. Sem isto, a
        // proxima acao do usuario mandaria de novo o `If-Match` velho e conflitaria de novo —
        // um carrinho que nunca mais aceita edicao ate o app ser recarregado.
        core.versions.remember(
          'cart',
          error.current === null ? null : cartWeakETag(error.current.id, error.current.version),
        );
      }
      throw error;
    }
  }

  /** A mutacao leva sempre a versao guardada; ausente, nao inventa `If-Match`. */
  function mutation(
    spec: Omit<ApiRequestSpec<CartResponse>, 'response' | 'ifMatch'>,
  ): Promise<CartResponse> {
    return run({ ...spec, response: CartResponseSchema, ifMatch: core.versions.current('cart') });
  }

  return {
    async get(signal) {
      return await run({
        method: 'GET',
        path: '/v1/cart',
        response: CartResponseSchema,
        ...(signal === undefined ? {} : { signal }),
      });
    },

    replace(body) {
      return mutation({ method: 'PUT', path: '/v1/cart', body });
    },

    addItem(body) {
      return mutation({ method: 'POST', path: '/v1/cart/items', body });
    },

    changeItem(itemId, body) {
      return mutation({
        method: 'PATCH',
        path: `/v1/cart/items/${encodeURIComponent(itemId)}`,
        body,
      });
    },

    removeItem(itemId) {
      return mutation({
        method: 'DELETE',
        path: `/v1/cart/items/${encodeURIComponent(itemId)}`,
      });
    },

    merge(body) {
      return mutation({ method: 'POST', path: '/v1/cart/merge', body });
    },

    applyCoupon(body) {
      return mutation({ method: 'POST', path: '/v1/cart/coupons', body });
    },

    removeCoupon(code) {
      return mutation({
        method: 'DELETE',
        path: `/v1/cart/coupons/${encodeURIComponent(code)}`,
      });
    },

    currentETag() {
      return core.versions.current('cart');
    },
  };
}
